import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, FileText, TrendingUp, Lightbulb } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const categories = [
  {
    icon: BookOpen,
    title: 'Strategic Perspectives',
    description: 'Articles exploring enterprise transformation, leadership effectiveness, and revenue architecture.',
    color: '#4DB848',
  },
  {
    icon: FileText,
    title: 'Transformation Frameworks',
    description: 'Practical frameworks used by SGA consultants including our Execution Model and Governance Framework.',
    color: '#4DB848',
  },
  {
    icon: TrendingUp,
    title: 'Industry Trends',
    description: 'Analysis of emerging developments in digital infrastructure, cybersecurity, and cloud ecosystems.',
    color: '#4DB848',
  },
  {
    icon: Lightbulb,
    title: 'Thought Leadership',
    description: 'Whitepapers and expert commentary on business transformation challenges.',
    color: '#4DB848',
  },
];

const placeholderArticles = [
  {
    category: 'Strategic Perspectives',
    title: 'Bridging the Execution Gap: Why Strategy Fails',
    excerpt: 'Understanding the critical factors that prevent organizations from translating strategic vision into operational reality.',
    date: 'Coming Soon',
    readTime: '8 min read',
  },
  {
    category: 'Transformation Frameworks',
    title: 'The BUILD-OPERATE-ORCHESTRATE Model',
    excerpt: 'A deep dive into SGA\'s proprietary transformation methodology and how it delivers sustainable business outcomes.',
    date: 'Coming Soon',
    readTime: '12 min read',
  },
  {
    category: 'Industry Trends',
    title: 'Enterprise Cloud Strategy in 2025',
    excerpt: 'Key trends shaping cloud adoption and how organizations can position themselves for success.',
    date: 'Coming Soon',
    readTime: '6 min read',
  },
  {
    category: 'Thought Leadership',
    title: 'Leadership in the Age of Transformation',
    excerpt: 'What it takes to lead organizations through complex transformation initiatives.',
    date: 'Coming Soon',
    readTime: '10 min read',
  },
  {
    category: 'Strategic Perspectives',
    title: 'Revenue Architecture for Modern Enterprises',
    excerpt: 'How to design revenue models that align with digital transformation initiatives.',
    date: 'Coming Soon',
    readTime: '7 min read',
  },
  {
    category: 'Industry Trends',
    title: 'Cybersecurity in the Enterprise Ecosystem',
    excerpt: 'Emerging security challenges and how organizations can build resilient frameworks.',
    date: 'Coming Soon',
    readTime: '9 min read',
  },
];

export const InsightsPage = () => {
  return (
    <div data-testid="insights-page">
      {/* Hero Banner - 1920 × 420px slim banner */}
      <section 
        className="relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url('/assets/banners/Insights_Banner.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          minHeight: '420px',
          height: '420px',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <motion.div 
            className="max-w-lg"
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1 } }
            }}
          >
            <motion.h1 
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="font-montserrat font-bold text-3xl md:text-4xl text-white mb-4"
            >
              Strategic Thinking for Modern Enterprises
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-inter text-base text-white leading-relaxed"
            >
              Insights and frameworks shaping the future of enterprise leadership 
              and digital transformation.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Knowledge Hub
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-6">
              Perspectives on Strategy and Transformation
            </h2>
            <p className="body-text">
              SGA STRAT publishes insights that help organizations navigate evolving 
              business landscapes. Our thought leadership is grounded in practical 
              experience and designed to drive real business impact.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.title}
                data-testid={`category-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 border border-slate-100 hover:shadow-lg transition-shadow duration-300"
              >
                <div 
                  className="w-12 h-12 flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${category.color}15` }}
                >
                  <category.icon className="w-6 h-6" style={{ color: category.color }} />
                </div>
                <h3 className="font-montserrat font-semibold text-lg text-[#0B1E2D] mb-2">
                  {category.title}
                </h3>
                <p className="font-inter text-sm text-slate-500">
                  {category.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-2 block">
                Latest Articles
              </span>
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-[#0B1E2D]">
                Featured Insights
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {placeholderArticles.map((article, index) => (
              <motion.article
                key={index}
                data-testid={`article-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white border border-slate-100 hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                {/* Placeholder Image */}
                <div className="aspect-[16/9] bg-slate-100 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0087B4]/20 to-[#4DB848]/20" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-[#0087B4]/40" />
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="font-inter text-xs font-medium text-[#0087B4] uppercase tracking-wider">
                      {article.category}
                    </span>
                    <span className="font-inter text-xs text-slate-400">
                      {article.readTime}
                    </span>
                  </div>
                  
                  <h3 className="font-montserrat font-semibold text-lg text-[#0B1E2D] mb-3 group-hover:text-[#0087B4] transition-colors">
                    {article.title}
                  </h3>
                  
                  <p className="font-inter text-sm text-slate-500 mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="font-inter text-xs text-slate-400">
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1 text-[#0087B4] font-inter text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Coming Soon Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 text-center p-8 bg-slate-50 border border-slate-200"
          >
            <Lightbulb className="w-12 h-12 mx-auto mb-4 text-[#4DB848]" />
            <h3 className="font-montserrat font-semibold text-xl text-[#0B1E2D] mb-2">
              More Insights Coming Soon
            </h3>
            <p className="font-inter text-slate-500 max-w-lg mx-auto">
              We're preparing a comprehensive library of strategic insights, frameworks, 
              and industry analysis. Stay tuned for valuable content that will help drive 
              your transformation journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0B1E2D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section text-white mb-6">
              Want to Discuss These Ideas?
            </h2>
            <p className="font-inter text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Connect with our experts to explore how these insights can be 
              applied to your organization's transformation journey.
            </p>
            <Link 
              to="/engage"
              data-testid="insights-cta"
              className="inline-flex items-center gap-2 bg-[#4DB848] hover:bg-[#3da03a] text-white px-10 py-4 font-montserrat font-bold tracking-wider transition-colors"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
